import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Undo2, Redo2, Save, RotateCw, Trash2, Copy,
  ZoomIn, ZoomOut, Maximize2, Minus, Plus, Crosshair, Frame,
  Square, Circle as CircleIcon, RectangleHorizontal,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { V2Shape } from "@/hooks/useFloorPlanV2";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

interface Props {
  canUndo: boolean;
  canRedo: boolean;
  dirty: boolean;
  saving: boolean;
  lastSavedAt: Date | null;
  selectedCount: number;
  
  zoom: number;
  onUndo: () => void;
  onRedo: () => void;
  onSave: () => void;
  
  onRotate: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomFit: () => void;
  onRecenter?: () => void;
  onCompactCanvas?: () => void;
  
  onSetShape: (shape: V2Shape) => void;
  singleSelection?: {
    width: number;
    height: number;
    shape: V2Shape;
    onResize: (width: number, height: number) => void;
  };
  /** Apply a preset size to all selected tables (S/M/L/XL). */
  onApplyPreset?: (size: number) => void;
  /** Scale all selected tables by factor (e.g. 0.9 / 1.1). */
  onScaleSelection?: (factor: number) => void;
}

const SAVE_LABELS = ['à l\'instant', 'il y a quelques sec.'];

export const V2Toolbar = ({
  canUndo, canRedo, dirty, saving, lastSavedAt,
  selectedCount, zoom,
  onUndo, onRedo, onSave, onRotate, onDuplicate, onDelete,
  onZoomIn, onZoomOut, onZoomFit, onRecenter, onCompactCanvas, onSetShape,
  singleSelection, onApplyPreset, onScaleSelection,
}: Props) => {
  const hasSel = selectedCount > 0;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center gap-1 bg-card/95 backdrop-blur border border-border rounded-lg p-1 shadow-sm flex-wrap">
        {/* Undo / Redo */}
        <Tooltip><TooltipTrigger asChild>
          <Button size="icon" variant="ghost" disabled={!canUndo} onClick={onUndo} className="h-9 w-9">
            <Undo2 className="h-4 w-4" />
          </Button>
        </TooltipTrigger><TooltipContent>Annuler (⌘Z)</TooltipContent></Tooltip>

        <Tooltip><TooltipTrigger asChild>
          <Button size="icon" variant="ghost" disabled={!canRedo} onClick={onRedo} className="h-9 w-9">
            <Redo2 className="h-4 w-4" />
          </Button>
        </TooltipTrigger><TooltipContent>Rétablir (⌘⇧Z)</TooltipContent></Tooltip>

        <Separator orientation="vertical" className="h-6" />

        {/* Selection actions */}
        <Tooltip><TooltipTrigger asChild>
          <Button size="icon" variant="ghost" disabled={!hasSel} onClick={onRotate} className="h-9 w-9">
            <RotateCw className="h-4 w-4" />
          </Button>
        </TooltipTrigger><TooltipContent>Pivoter +15°</TooltipContent></Tooltip>

        <Tooltip><TooltipTrigger asChild>
          <Button size="icon" variant="ghost" disabled={!hasSel} onClick={onDuplicate} className="h-9 w-9">
            <Copy className="h-4 w-4" />
          </Button>
        </TooltipTrigger><TooltipContent>Dupliquer (⌘D)</TooltipContent></Tooltip>


        {/* Shape picker */}
        <DropdownMenu>
          <Tooltip><TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" disabled={!hasSel} className="h-9 w-9">
                <Square className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger><TooltipContent>Forme</TooltipContent></Tooltip>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => onSetShape('rect')}><RectangleHorizontal className="h-4 w-4 mr-2" /> Rectangle</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSetShape('square')}><Square className="h-4 w-4 mr-2" /> Carré</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSetShape('round')}><CircleIcon className="h-4 w-4 mr-2" /> Rond</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSetShape('oval')}><CircleIcon className="h-4 w-4 mr-2" /> Ovale</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSetShape('bar')}><RectangleHorizontal className="h-4 w-4 mr-2" /> Bar / banc</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {hasSel && (
          <>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-1 px-1" data-no-drag>
              {/* Preset sizes — apply to all selected, shape-aware via parent */}
              {onApplyPreset && (
                <>
                  {[
                    { label: 'S', size: 64 },
                    { label: 'M', size: 80 },
                    { label: 'L', size: 104 },
                    { label: 'XL', size: 128 },
                  ].map(p => (
                    <Tooltip key={p.label}><TooltipTrigger asChild>
                      <Button
                        size="sm" variant="ghost"
                        className="h-9 w-9 px-0 text-xs font-semibold tabular-nums"
                        onClick={() => onApplyPreset(p.size)}
                      >
                        {p.label}
                      </Button>
                    </TooltipTrigger><TooltipContent>Taille {p.label} ({p.size}px)</TooltipContent></Tooltip>
                  ))}
                </>
              )}

              {/* Scale ±10% — works on multi-selection */}
              {onScaleSelection && (
                <>
                  <Tooltip><TooltipTrigger asChild>
                    <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => onScaleSelection(0.9)}>
                      <Minus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger><TooltipContent>Réduire de 10% ([)</TooltipContent></Tooltip>
                  <Tooltip><TooltipTrigger asChild>
                    <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => onScaleSelection(1.1)}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger><TooltipContent>Agrandir de 10% (])</TooltipContent></Tooltip>
                </>
              )}

              {/* Precise W×H inputs — single selection only */}
              {singleSelection && (
                <>
                  <span className="text-xs text-muted-foreground ml-1">W</span>
                  <Input
                    type="number"
                    min={32}
                    step={8}
                    value={Math.round(singleSelection.width)}
                    onChange={(e) => {
                      const w = Math.max(32, Number(e.target.value) || 32);
                      const h = (singleSelection.shape === 'square' || singleSelection.shape === 'round')
                        ? w : singleSelection.height;
                      singleSelection.onResize(w, h);
                    }}
                    className="h-9 w-14 text-xs px-2 tabular-nums"
                  />
                  <span className="text-xs text-muted-foreground">×</span>
                  <Input
                    type="number"
                    min={32}
                    step={8}
                    value={Math.round(singleSelection.height)}
                    onChange={(e) => {
                      const h = Math.max(32, Number(e.target.value) || 32);
                      const w = (singleSelection.shape === 'square' || singleSelection.shape === 'round')
                        ? h : singleSelection.width;
                      singleSelection.onResize(w, h);
                    }}
                    className="h-9 w-14 text-xs px-2 tabular-nums"
                  />
                </>
              )}
            </div>
          </>
        )}

        <Separator orientation="vertical" className="h-6" />

        {/* Zoom */}
        <Tooltip><TooltipTrigger asChild>
          <Button size="icon" variant="ghost" onClick={onZoomOut} className="h-9 w-9">
            <ZoomOut className="h-4 w-4" />
          </Button>
        </TooltipTrigger><TooltipContent>Zoom −</TooltipContent></Tooltip>

        <button
          type="button"
          onClick={onZoomFit}
          className="text-xs text-muted-foreground hover:text-foreground px-2 min-w-[48px] tabular-nums h-9"
        >
          {Math.round(zoom * 100)}%
        </button>

        <Tooltip><TooltipTrigger asChild>
          <Button size="icon" variant="ghost" onClick={onZoomIn} className="h-9 w-9">
            <ZoomIn className="h-4 w-4" />
          </Button>
        </TooltipTrigger><TooltipContent>Zoom +</TooltipContent></Tooltip>

        <Tooltip><TooltipTrigger asChild>
          <Button size="icon" variant="ghost" onClick={onZoomFit} className="h-9 w-9">
            <Maximize2 className="h-4 w-4" />
          </Button>
        </TooltipTrigger><TooltipContent>Ajuster</TooltipContent></Tooltip>

        {onRecenter && (
          <Tooltip><TooltipTrigger asChild>
            <Button size="icon" variant="ghost" onClick={onRecenter} className="h-9 w-9">
              <Crosshair className="h-4 w-4" />
            </Button>
          </TooltipTrigger><TooltipContent>Recentrer le plan</TooltipContent></Tooltip>
        )}

        {onCompactCanvas && (
          <Tooltip><TooltipTrigger asChild>
            <Button size="icon" variant="ghost" onClick={onCompactCanvas} className="h-9 w-9">
              <Frame className="h-4 w-4" />
            </Button>
          </TooltipTrigger><TooltipContent>Compacter le plan sur les tables</TooltipContent></Tooltip>
        )}

        <Separator orientation="vertical" className="h-6" />

        {/* Delete */}
        <Tooltip><TooltipTrigger asChild>
          <Button size="icon" variant="ghost" disabled={!hasSel} onClick={onDelete} className="h-9 w-9 text-destructive hover:text-destructive">
            <Trash2 className="h-4 w-4" />
          </Button>
        </TooltipTrigger><TooltipContent>Retirer du plan (Suppr)</TooltipContent></Tooltip>

        <Separator orientation="vertical" className="h-6" />

        {/* Save status */}
        <div className="text-xs text-muted-foreground px-2 min-w-[80px] flex items-center gap-1">
          <Save className="h-3 w-3" />
          {saving ? 'Sauvegarde…' : dirty ? 'Modifié' : (lastSavedAt ? 'Sauvegardé' : 'À jour')}
        </div>
      </div>
    </TooltipProvider>
  );
};
